import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Marker,
  Polygon,
  useMap,
  useMapEvents,
} from "react-leaflet";
import type { GeoJsonObject } from "geojson";
import type {
  LeafletEvent,
  LatLngExpression,
  Map as LeafletMap,
  Polygon as LeafletPolygon,
} from "leaflet";
import L from "leaflet";
import { OpenStreetMapProvider } from "leaflet-geosearch";
import { LoaderCircle, LocateFixed, MapPin, Search } from "lucide-react";
import { Button } from "../../button";
import { CustomModal } from "../../modal";
import type { ModalAction } from "../../modal";
import MapViewportController from "./MapViewportController";
import MapGeoJsonBoundaryLayer from "./MapGeoJsonBoundaryLayer";
import MapSurface from "./MapSurface";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";
import "./map.css";

export interface LatLng {
  lat: number;
  lng: number;
}
export interface LocationPickerValue {
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  polygon?: LatLng[];
}
export interface LocationPickerProps {
  value?: LocationPickerValue;
  enableSearch?: boolean;
  enableCurrentLocation?: boolean;
  enableMarker?: boolean;
  enablePolygon?: boolean;
  disabled?: boolean;
  onChange?: (value: LocationPickerValue) => void;
  className?: string;
  lat?: number;
  lon?: number;
  address?: string;
  isOpen?: boolean;
  onClose?: () => void;
  onCancel?: () => void;
  modalAction?: ModalAction;
  restrictToUSA?: boolean;
  geoJson?: GeoJsonObject;
  onSelect?: (lat: number, lon: number, address: string) => void;
}

const DEFAULT_CENTER: LatLng = { lat: 39.8283, lng: -98.5795 };
const isCoordinate = (value?: number): value is number =>
  typeof value === "number" && Number.isFinite(value);
const getCoordinate = (value?: LocationPickerValue): LatLng | undefined =>
  isCoordinate(value?.latitude) && isCoordinate(value?.longitude)
    ? { lat: value.latitude, lng: value.longitude }
    : undefined;
const normalizePolygon = (polygon: LatLng[]): LatLng[] =>
  polygon.filter((point) => isCoordinate(point.lat) && isCoordinate(point.lng));

const MapInteractions = ({
  enabled,
  onSelect,
}: {
  enabled: boolean;
  onSelect: (point: LatLng) => void | Promise<void>;
}) => {
  useMapEvents({
    click: (event) => {
      if (enabled) onSelect({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
};

const PolygonManager = ({
  enabled,
  polygon,
  disabled,
  onChange,
}: {
  enabled: boolean;
  polygon: LatLng[];
  disabled: boolean;
  onChange: (polygon: LatLng[]) => void;
}) => {
  const map = useMap() as LeafletMap;
  const polygonLayer = useRef<LeafletPolygon | null>(null);
  const readLayer = useCallback(
    (layer: LeafletPolygon): LatLng[] =>
      layer.getLatLngs().flatMap((ring) => {
        const points =
          Array.isArray(ring) && (ring.length === 0 || "lat" in ring[0])
            ? (ring as L.LatLng[])
            : [];
        return points.map((point) => ({ lat: point.lat, lng: point.lng }));
      }),
    [],
  );
  useEffect(() => {
    if (!enabled || disabled || !map.pm) return;
    map.pm.addControls({
      position: "topleft",
      drawCircle: false,
      drawCircleMarker: false,
      drawMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawText: false,
      editMode: true,
      dragMode: false,
      cutPolygon: false,
      removalMode: true,
    });
    const handleCreate = (event: LeafletEvent & { layer?: L.Layer }) => {
      if (!(event.layer instanceof L.Polygon)) return;
      if (polygonLayer.current && map.hasLayer(polygonLayer.current))
        map.removeLayer(polygonLayer.current);
      polygonLayer.current = event.layer as LeafletPolygon;
      onChange(readLayer(polygonLayer.current));
    };
    const handleEdit = (event: LeafletEvent & { layer?: L.Layer }) => {
      if (event.layer instanceof L.Polygon) onChange(readLayer(event.layer as LeafletPolygon));
    };
    const handleRemove = (event: LeafletEvent & { layer?: L.Layer }) => {
      if (event.layer instanceof L.Polygon) {
        polygonLayer.current = null;
        onChange([]);
      }
    };
    map.on("pm:create", handleCreate);
    map.on("pm:edit", handleEdit);
    map.on("pm:remove", handleRemove);
    return () => {
      map.off("pm:create", handleCreate);
      map.off("pm:edit", handleEdit);
      map.off("pm:remove", handleRemove);
      map.pm?.removeControls();
    };
  }, [disabled, enabled, map, onChange, readLayer]);
  if (!enabled || polygon.length < 3) return null;
  return (
    <Polygon
      positions={polygon.map(({ lat, lng }) => [lat, lng] as LatLngExpression)}
      pathOptions={{ color: "#2563eb", fillOpacity: 0.2 }}
      eventHandlers={{
        add: (event) => {
          polygonLayer.current = event.target as LeafletPolygon;
        },
      }}
    />
  );
};

const LocationPicker = ({
  value: suppliedValue,
  enableSearch = true,
  enableCurrentLocation = true,
  enableMarker = true,
  enablePolygon = false,
  disabled = false,
  onChange,
  className = "",
  lat,
  lon,
  address,
  isOpen,
  onClose,
  onCancel,
  modalAction,
  geoJson,
  onSelect,
}: LocationPickerProps) => {
  const externalValue = useMemo(
    () => suppliedValue ?? { latitude: lat, longitude: lon, address },
    [address, lat, lon, suppliedValue],
  );
  const [draftValue, setDraftValue] = useState(externalValue);
  const confirmValue = useCallback(
    (next: LocationPickerValue) => {
      onChange?.(next);
      if (onSelect && isCoordinate(next.latitude) && isCoordinate(next.longitude))
        onSelect(next.latitude, next.longitude, next.address ?? "");
    },
    [onChange, onSelect],
  );
  const [query, setQuery] = useState(externalValue.address ?? "");
  const [error, setError] = useState<string>();
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const requestRef = useRef<AbortController>();
  const coordinate = getCoordinate(draftValue);
  const center = coordinate ?? DEFAULT_CENTER;
  const provider = useMemo(() => new OpenStreetMapProvider(), []);
  useEffect(() => {
    setDraftValue(externalValue);
    setQuery(externalValue.address ?? "");
  }, [externalValue]);
  useEffect(() => () => requestRef.current?.abort(), []);
  const updateCoordinate = useCallback(
    (
      point: LatLng,
      details?: Pick<LocationPickerValue, "address" | "city" | "state" | "postalCode">,
    ) =>
      setDraftValue((currentValue) => ({
        ...currentValue,
        ...details,
        latitude: point.lat,
        longitude: point.lng,
      })),
    [],
  );
  const reverseGeocode = useCallback(
    async (
      point: LatLng,
      signal: AbortSignal,
    ): Promise<
      Pick<LocationPickerValue, "address" | "city" | "state" | "postalCode"> | undefined
    > => {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${point.lat}&lon=${point.lng}`,
        { signal, headers: { Accept: "application/json" } },
      );
      if (!response.ok) throw new Error("Reverse geocoding failed");
      const data: unknown = await response.json();
      if (typeof data !== "object" || data === null) return undefined;
      const record = data as Record<string, unknown>;
      const address = record.address;
      const parts =
        typeof address === "object" && address !== null ? (address as Record<string, unknown>) : {};
      const stringPart = (key: string) => (typeof parts[key] === "string" ? parts[key] : undefined);
      const road = stringPart("road");
      const houseNumber = stringPart("house_number");

      return {
        address:
          [houseNumber, road].filter(Boolean).join(" ") ||
          (typeof record.display_name === "string" ? record.display_name : undefined),
        city:
          stringPart("city") ??
          stringPart("town") ??
          stringPart("village") ??
          stringPart("municipality"),
        state: stringPart("state"),
        postalCode: stringPart("postcode"),
      };
    },
    [],
  );
  const search = async () => {
    if (!query.trim() || disabled) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setError(undefined);
    setIsSearching(true);
    try {
      const results = await provider.search({ query: query.trim() });
      if (controller.signal.aborted) return;
      const result = results[0];
      if (!result || !Number.isFinite(result.y) || !Number.isFinite(result.x)) {
        setError("No location was found.");
        return;
      }
      const point = { lat: result.y, lng: result.x };
      const details = await reverseGeocode(point, controller.signal);
      if (controller.signal.aborted) return;
      updateCoordinate(point, details ?? { address: result.label });
      setQuery(details?.address ?? result.label);
    } catch (cause: unknown) {
      if (!controller.signal.aborted)
        setError(cause instanceof Error ? cause.message : "Unable to search for that location.");
    } finally {
      if (!controller.signal.aborted) setIsSearching(false);
    }
  };
  const resolveCoordinate = useCallback(
    async (point: LatLng) => {
      requestRef.current?.abort();
      const controller = new AbortController();
      requestRef.current = controller;

      try {
        const details = await reverseGeocode(point, controller.signal);
        if (controller.signal.aborted) return;
        setError(undefined);
        updateCoordinate(point, details);
        if (details?.address) setQuery(details.address);
      } catch (cause: unknown) {
        if (!controller.signal.aborted) {
          updateCoordinate(point);
          setError(cause instanceof Error ? cause.message : "Unable to resolve this location.");
        }
      }
    },
    [reverseGeocode, updateCoordinate],
  );

  const locate = () => {
    if (disabled || !navigator.geolocation) {
      setError("Location services are not supported by this browser.");
      return;
    }
    setError(undefined);
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const point = { lat: coords.latitude, lng: coords.longitude };
        const controller = new AbortController();
        requestRef.current?.abort();
        requestRef.current = controller;
        try {
          const details = await reverseGeocode(point, controller.signal);
          updateCoordinate(point, details);
          if (details?.address) setQuery(details.address);
        } catch (cause: unknown) {
          if (!controller.signal.aborted) {
            updateCoordinate(point);
            setError(
              cause instanceof Error ? cause.message : "Unable to resolve the current address.",
            );
          }
        } finally {
          setIsLocating(false);
        }
      },
      (positionError) => {
        setIsLocating(false);
        setError(
          positionError.code === 1
            ? "Location permission was denied."
            : positionError.code === 3
              ? "Location request timed out."
              : "Unable to determine your location.",
        );
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };
  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    setDraftValue(externalValue);
    setQuery(externalValue.address ?? "");
    setError(undefined);
    onCancel?.();
    onClose?.();
  }, [externalValue, onCancel, onClose]);
  const handleConfirm = useCallback(() => {
    if (enableMarker && !coordinate) {
      setError("Select a point on the map before confirming.");
      return;
    }

    confirmValue(draftValue);
    modalAction?.onClick();
  }, [confirmValue, coordinate, draftValue, enableMarker, modalAction]);
  const isConfirmDisabled =
    disabled ||
    Boolean(modalAction?.isDisabled) ||
    Boolean(modalAction?.isLoading) ||
    (enableMarker && !coordinate);
  const content = (
    <div className={`location-picker ${className}`}>
      <div className="location-picker__header">
        <span className="location-picker__eyebrow">Map selection</span>
        <h2>Location picker</h2>
        <p>Search for an address or select a precise point on the map.</p>
      </div>
      {enableSearch && (
        <form
          className="location-picker__search"
          onSubmit={(event) => {
            event.preventDefault();
            void search();
          }}
        >
          <label className="location-picker__search-field">
            <Search aria-hidden="true" />
            <input
              value={query}
              disabled={disabled || isSearching}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by address, city, or ZIP code"
              aria-label="Search location"
            />
          </label>
          <Button
            type="submit"
            className="location-picker__search-button"
            disabled={disabled || isSearching || !query.trim()}
          >
            {isSearching && (
              <LoaderCircle
                className="location-picker__spinner"
                aria-hidden="true"
              />
            )}
            {isSearching ? "Searching" : "Search"}
          </Button>
        </form>
      )}
      {enableCurrentLocation && (
        <Button
          type="button"
          variant="outline"
          className="location-picker__current"
          onClick={locate}
          disabled={disabled || isLocating}
        >
          {isLocating ? (
            <LoaderCircle
              className="location-picker__spinner"
              aria-hidden="true"
            />
          ) : (
            <LocateFixed aria-hidden="true" />
          )}
          {isLocating ? "Locating" : "Use current location"}
        </Button>
      )}
      {error && (
        <p
          className="location-picker__error"
          role="alert"
        >
          {error}
        </p>
      )}
      <div className="location-picker__map">
        <MapSurface
          initialViewport={{ center: [center.lat, center.lng], zoom: coordinate ? 13 : 4 }}
          mode={disabled ? "readonly" : "interactive"}
          style={{ height: "100%", width: "100%" }}
        >
          {coordinate && (
            <MapViewportController
              viewport={{ center: [coordinate.lat, coordinate.lng] }}
              minimumZoom={13}
            />
          )}
          <MapGeoJsonBoundaryLayer data={geoJson} fitOnMount={!coordinate} />
          <MapInteractions
            enabled={enableMarker && !disabled}
            onSelect={resolveCoordinate}
          />
          {enableMarker && coordinate && (
            <Marker
              position={[coordinate.lat, coordinate.lng]}
              draggable={!disabled}
              eventHandlers={{
                dragend: (event) => {
                  const position = event.target.getLatLng();
                  void resolveCoordinate({ lat: position.lat, lng: position.lng });
                },
              }}
            />
          )}
          {
            <PolygonManager
              enabled={enablePolygon}
              disabled={disabled}
              polygon={normalizePolygon(draftValue.polygon ?? [])}
              onChange={(polygon) =>
                setDraftValue((currentValue) => ({ ...currentValue, polygon }))
              }
            />
          }
        </MapSurface>
      </div>
      <div className="location-picker__details">
        <div className="location-picker__address">
          <span>Address</span>
          <strong>
            <MapPin aria-hidden="true" />
            <span>{draftValue.address || "Select a point on the map"}</span>
          </strong>
        </div>
        <div>
          <span>Latitude</span>
          <strong>
            {isCoordinate(draftValue.latitude) ? draftValue.latitude.toFixed(6) : "-"}
          </strong>
        </div>
        <div>
          <span>Longitude</span>
          <strong>
            {isCoordinate(draftValue.longitude) ? draftValue.longitude.toFixed(6) : "-"}
          </strong>
        </div>
      </div>
      <div className="location-picker__actions">
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={disabled || Boolean(modalAction?.isLoading)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          aria-busy={modalAction?.isLoading || undefined}
        >
          {modalAction?.label ?? "Confirm"}
        </Button>
      </div>
    </div>
  );

  if (isOpen === undefined) return content;

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={handleCancel}
      maxWidth="md"
      contentClassName="location-picker-modal-content"
    >
      {content}
    </CustomModal>
  );
};
export default LocationPicker;
export { LocationPicker };
